import React from 'react';
import { Link } from 'react-router-dom';
import { Separator } from '@/components/ui/separator';
import { Linkedin, Twitter, Youtube, Mail } from 'lucide-react';
import { IN_APP_ROUTES } from '@/constants/urls';

const footerLinks = {
  Product: [
    { label: 'Rate Parity Monitoring', href: '/#features' },
    { label: 'Competitive Analysis', href: '/#features' },
    { label: 'AI-Powered Insights', href: '/#features' },
    { label: 'Advanced Analytics', href: '/#features' },
  ],
  Solutions: [
    { label: 'Revenue Managers', href: IN_APP_ROUTES.REGISTER_PAGE },
    { label: 'Hotel Chains', href: IN_APP_ROUTES.REGISTER_PAGE },
    { label: 'Independent Hotels', href: IN_APP_ROUTES.REGISTER_PAGE },
    { label: 'Pay As You Go', href: '/#pricing' },
  ],
  Resources: [
    { label: 'How It Works', href: '/#how-it-works' },
    { label: 'Pricing Plans', href: '/#pricing' },
    { label: 'Help Center', href: IN_APP_ROUTES.LOGIN_PAGE },
    { label: 'Get Started', href: IN_APP_ROUTES.REGISTER_PAGE },
  ],
  Company: [
    { label: 'About Prowen', href: 'https://prowentech.com/' },
    { label: 'Contact Us', href: 'mailto:support@prowentech.com' },
    { label: 'Privacy Policy', href: IN_APP_ROUTES.PRIVACY_POLICY, route: true },
    { label: 'Terms & Conditions', href: IN_APP_ROUTES.TERMS_AND_CONDITIONS, route: true },
    { label: 'Cancellation & Refund', href: IN_APP_ROUTES.CANCELLATION_POLICY, route: true },
  ],
};

const socialLinks = [
  { icon: Linkedin, href: 'https://www.linkedin.com/company/prowen-technologies/', label: 'LinkedIn' },
  // { icon: Twitter, href: '#', label: 'Twitter' },
  // { icon: Youtube, href: '#', label: 'YouTube' },
  { icon: Mail, href: 'mailto:support@prowentech.com', label: 'Email' },
];

function FooterLink({ link }) {
  if (link.route) {
    return (
      <Link to={link.href} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
        {link.label}
      </Link>
    );
  }
  return (
    <a href={link.href} target='_blank' className="text-sm text-muted-foreground hover:text-foreground transition-colors">
      {link.label}
    </a>
  );
}

export default function Footer() {
  return (
    <footer className="bg-card border-t border-border" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-12 lg:py-16">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-8 lg:gap-12">
            {/* Brand Column */}
            <div className="col-span-2 md:col-span-4 lg:col-span-1 mb-8 lg:mb-0">
              <a href="/" className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 rounded-xl bg-primary text-primary-foreground flex items-center justify-center font-heading font-bold text-lg">
                  P
                </div>
                <span className="font-heading font-semibold text-xl text-foreground">
                  Prowen Technologies
                </span>
              </a>
              <p className="text-sm text-muted-foreground mb-6 max-w-xs">
                Make Smarter Pricing Decisions with a Hotel Analytics Platform Trusted by 500+ Properties Worldwide, built by a leading <a target='_blank' href='https://prowentech.com/'> SaaS company in Coimbatore.</a>
              </p>
              {/* Social Links */}
              <div className="flex items-center gap-3">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.href}
                    aria-label={social.label}
                    className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center text-muted-foreground hover:bg-primary hover:text-primary-foreground transition-colors"
                  >
                    <social.icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
            </div>

            {/* Link Columns */}
            {Object.entries(footerLinks).map(([category, links]) => (
              <div key={category}>
                <h3 className="font-semibold text-foreground mb-4">{category}</h3>
                <ul className="space-y-3">
                  {links.map((link, index) => (
                    <li key={index}>
                      <FooterLink link={link} />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <Separator className="bg-border" />

        {/* Bottom Footer */}
        <div className="py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">
            &copy; {new Date().getFullYear()} Prowen Technologies India P Ltd. All rights reserved.
          </p>
          <div className="flex items-center gap-6 text-sm">
            <Link to={IN_APP_ROUTES.PRIVACY_POLICY} className="text-muted-foreground hover:text-foreground transition-colors">
              Privacy Policy
            </Link>
            <Link to={IN_APP_ROUTES.TERMS_AND_CONDITIONS} className="text-muted-foreground hover:text-foreground transition-colors">
              Terms & Conditions
            </Link>
            <Link to={IN_APP_ROUTES.CANCELLATION_POLICY} className="text-muted-foreground hover:text-foreground transition-colors">
              Cancellation & Refund
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
